import { normalizeTags } from "@growth/schema";
import { X } from "lucide-react";
import { type KeyboardEvent, useState } from "react";

type Props = {
  id?: string;
  value: string[];
  onChange: (tags: string[]) => void;
};

/** Type a tag and press Enter or comma; Backspace on an empty input removes the last one. */
export const TagsInput = ({ id, value, onChange }: Props) => {
  const [draft, setDraft] = useState("");

  const commit = () => {
    if (!draft.trim()) return;
    onChange(normalizeTags([...value, ...draft.split(",")]));
    setDraft("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit();
    } else if (event.key === "Backspace" && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-line bg-white px-2 py-1.5 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20">
      {value.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded-full bg-paper px-2 py-0.5 text-xs text-ink"
        >
          {tag}
          <button
            type="button"
            aria-label={`Remove tag ${tag}`}
            onClick={() => onChange(value.filter((t) => t !== tag))}
            className="text-muted hover:text-ink"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        id={id}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={value.length === 0 ? "Add tags…" : ""}
        disabled={value.length >= 10}
        className="min-w-24 flex-1 bg-transparent text-sm outline-none placeholder:text-muted/70"
      />
    </div>
  );
};
